import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { FocusTrap } from "focus-trap-react";
import BodyScrollLockWithGap from "@default/lib/body-scroll-lock-with-gap";
import { FormattedMessage } from "react-intl";
import DashedBorder from "@default/general-components/dashed-border";
import { motion } from "framer-motion";
import RichText from "@default/general-components/rich-text";
import Image from "../../../image/image";
import Hyphenate from "@default/general-components/custom-hyphenation";
import classNames from "classnames";
import useElementSize from "@default/hooks/use-element-size";

const CardTeaserOverlay = ({ content, onClose, color }) => {
  const [overlayInnerRef, { height: overlayInnerHeight }] = useElementSize();
  const [viewportHeight, setViewportHeight] = useState(() =>
    typeof window === "undefined" ? 0 : window.innerHeight
  );

  useEffect(() => {
    const handleResize = () => setViewportHeight(window.innerHeight);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (typeof document === "undefined") {
    return null;
  }

  const exceedsViewport = overlayInnerHeight > viewportHeight;

  return createPortal(
    <FocusTrap
      focusTrapOptions={{
        onDeactivate: onClose,
        escapeDeactivates: true,
        clickOutsideDeactivates: true,
      }}
    >
      <div
        className="position-wrapper on-card-teaser"
        style={color ? { "--card-color": `var(--c-${color})` } : undefined}
      >
        <motion.div
          className="overlay-backdrop"
          initial={{
            backdropFilter: "blur(0px)",
            background: "rgba(255, 255, 255, 0)",
          }}
          animate={{
            backdropFilter: "blur(20px)",
            background: "rgba(255, 255, 255, 0.25)",
          }}
          exit={{
            backdropFilter: "blur(0px)",
            background: "rgba(255, 255, 255, 0)",
          }}
          transition={{ duration: 0.7, ease: "easeOut" }}
        />
        <BodyScrollLockWithGap />
        <motion.div
          className="card-teaser-overlay"
          role="dialog"
          aria-modal="true"
          onClick={(e) => e.stopPropagation()}
        >
          <motion.button
            className="close"
            onClick={onClose}
            id="close-overlay-menu"
            initial={{
              scale: 0,
            }}
            animate={{
              scale: 1,
            }}
            exit={{
              scale: 0,
            }}
          >
            <DashedBorder blurredBackdrop />
            <div className="bar" />
            <div className="bar" />

            <span className="sr-only">
              <FormattedMessage id="accessibility.close_modal" />
            </span>
          </motion.button>

          <div className="content">
            <motion.div
              ref={overlayInnerRef}
              className={classNames("overlay-inner", {
                "exceeds-viewport": exceedsViewport,
              })}
              initial={{ y: "200%", scale: 0.7 }}
              animate={{ y: "0", scale: 1 }}
              exit={{ y: "-200%", scale: 0.7 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
            >
              {content.fieldImageEnhancedRawField?.first.entity
                ?.fieldMediaImage && (
                <Image
                  data={
                    content.fieldImageEnhancedRawField?.first.entity
                      ?.fieldMediaImage
                  }
                  credit={
                    content.fieldImageEnhancedRawField?.first.entity
                      ?.fieldCredits
                  }
                />
              )}
              <div className="text-wrapper">
                {content.fieldHeading && (
                  <h3 className="heading headline-m">
                    <Hyphenate>{content.fieldHeading}</Hyphenate>
                  </h3>
                )}
                {content.fieldText && (
                  <RichText className="text" html={content.fieldText} />
                )}
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </FocusTrap>,
    document.getElementById("modal-wrapper")
  );
};

export default CardTeaserOverlay;
